import React from 'react';
import Tooltip from '@mui/material/Tooltip';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import { alpha } from '@mui/system/colorManipulator';
import { useTheme } from '@mui/system';
import { useFormContext, useFieldArray } from 'react-hook-form';
import RecipientItem from '#component/shipment/form/notification/recipient-item';
import { setAfterSubmitAction, nextStep } from '#action/shipment/form';
import { submitAndGoBack } from '#component/shipment/form/use-step-submit';
import {
  StyledPaper,
  StyledTitle,
  AddBtnCnt,
  AddBtn,
  ButtonCnt,
  StyledButton
} from './style';
import { useIntersection } from '#hooks';

const RecipientItems = props => {
  const { fields, remove, disabled } = props;

  return fields && fields.map((field, index) => (
    <RecipientItem
      key={field.id || index}
      selectedRecipients={fields}
      index={index}
      member={`recipients[${index}]`}
      remove={remove}
      disabled={disabled || !field.editable}
    />
  ));
};

const intersectionOptions = {
  rootMargin: '-69px',
  threshold: [1]
};

/**
 * Renders form with submit, go back
 * and add user buttons.
 * @returns {React.FC}
 */

const Form = props => {
  const theme = useTheme();
  const methods = useFormContext();
  const { control, handleSubmit } = methods || {};
  const { fields, append, remove } = useFieldArray({ control, name: 'recipients' });

  const {
    submitting,
    shipment,
    dispatch,
    editable,
    SaveButton = () => null,
    CancelButton = () => null,
    onFormSubmit,
    isCreateMode
  } = props;

  const { sentinelRef, isIntersecting } = useIntersection(intersectionOptions);

  const addRecipientItem = () => {
    append({ value: '', email: null, id: null, type: 'email', editable: true });
  };

  const onBackClick = () =>
    submitAndGoBack(dispatch, methods, onFormSubmit, { validate: !isCreateMode });

  const onNextClick = () => {
    const next = () => {
      dispatch(nextStep());
    };

    dispatch(setAfterSubmitAction(next));
  };

  return (
    <form autoComplete="off" onSubmit={handleSubmit?.(onFormSubmit)}>
      <StyledPaper elevation={0}>
        <StyledTitle variant="h6" gutterBottom>
          Notification recipients
        </StyledTitle>
        <RecipientItems
          fields={fields}
          remove={remove}
          disabled={!editable}
        />
        <AddBtnCnt>
          <AddBtn
            type="button"
            size="small"
            disabled={!editable}
            onClick={addRecipientItem}
          >
            <Tooltip title="Add recipient">
              <AddCircleOutlineRoundedIcon
                style={{ color: alpha(theme.palette.secondary.main, editable ? 1 : 0.6) }}
              />
            </Tooltip>
          </AddBtn>
        </AddBtnCnt>
      </StyledPaper>
      <div ref={sentinelRef} />
      <ButtonCnt hasBorderTop={!isIntersecting}>
        <StyledButton as={CancelButton} />
        <StyledButton
          disabled={submitting}
          type="button"
          variant="outlined"
          onClick={onBackClick}
        >
          Back
        </StyledButton>
        <StyledButton
          disabled={submitting}
          variant="outlined"
          type="submit"
          onClick={onNextClick}
        >
          Next
        </StyledButton>
        {shipment.id && editable && (
        <StyledButton as={SaveButton} disabled={submitting} />
        )}
      </ButtonCnt>
    </form>
  );
};

export default Form;
